import { LocationPackage } from './location-package';
import { ProgressReporter } from './progress-reporter';
import { CarePackage, Location, Municipality } from '@/client/models';
import { WizardPart } from './wizard-part';

export interface StepData {
    packages?: LocationPackage[];
    municipality?: string;
}

export interface LocationStepCallback {
    carePackageSelected(carePackage: LocationPackage): void;
    carePackageDeselected(carePackage: LocationPackage): void;
}

export class LocationStepProvider {
    private data: StepData;
    private reporter: ProgressReporter;
    private callback: LocationStepCallback;
    private municipalities: Municipality[];

    constructor(data: StepData, reporter: ProgressReporter, callback: LocationStepCallback) {
        this.data = data;
        this.reporter = reporter;
        this.callback = callback;

        this.initialize = this.initialize.bind(this);
        this.isLocationSelected = this.isLocationSelected.bind(this);
        this.isLocationPackageSelected = this.isLocationPackageSelected.bind(this);
        this.toggleLocationPackage = this.toggleLocationPackage.bind(this);
        this.getMunicipalities = this.getMunicipalities.bind(this);
        this.setMunicipality = this.setMunicipality.bind(this);
        this.getSelectedMunicipality = this.getSelectedMunicipality.bind(this);

        if (data.municipality != null) {
            this.data.municipality = data.municipality;
        }
    }

    public initialize(municipalities: Municipality[]): void { 
        if (this.data.packages == null) {
            this.data.packages = new Array<LocationPackage>();
        }
        this.municipalities = municipalities;
    }

    public hasSubsidyPlace(): boolean {
        return this.data.packages.some(x => x.isSubsidyPlace);
    }

    public getMunicipalities(): Municipality[] {
        return this.municipalities;
    }

    public getSelectedMunicipality(): Municipality {
        return this.municipalities?.find(x => x.municipalityId === this.data.municipality);
    }

    public setMunicipality(municipality: string): void {
        this.data.municipality = municipality;
        this.changed();
    }

    private changed(): void {
        this.reporter.partChanged(WizardPart.locations, true);
    }

    public changedNoAutoComplete(): void {
        this.reporter.partChanged(WizardPart.locations, false);
    }

    public isLocationSelected(locationId: string): boolean {
        return this.data.packages.some(x => x.locationId === locationId);
    }

    public isLocationPackageSelected(locationId: string, carePackageId: string): boolean {
        return this.data.packages.some(x => x.locationId === locationId && x.carePackageId === carePackageId);
    }

    public findLocationPackage(locationId: string, carePackageId: string): { index: number; carePackage: LocationPackage } {
        const index = this.data.packages.findIndex(x => x.locationId === locationId && x.carePackageId === carePackageId);
        const carePackage = index >= 0 ? this.data.packages[index] : undefined;
        return { index, carePackage };
    }

    public findPreferredLocationIndex(locationId: string): number {
        const selectedLocation = {};
        let indexLocation = 1;
        for (let i = 0; i < this.data.packages.length; i++) {
            const pkg = this.data.packages[i];
            if (pkg.locationId === locationId) {
                return indexLocation;
            }
            if (selectedLocation[pkg.locationId]) {
                selectedLocation[pkg.locationId] = {};
                indexLocation++;
            }
        }
        return -1;
    }


    public toggleLocationPackage(location: Location, carePackage: CarePackage): void {
        const { index } = this.findLocationPackage(location.locationId, carePackage.carePackageId);
        if (index >= 0) {
            const removed = this.data.packages.splice(index, 1);
            this.callback.carePackageDeselected(removed[0]);
        } else {
            const locationPackage: LocationPackage = { locationId: location.locationId, locationName: location.name,
                 carePackageId: carePackage.carePackageId, carePackagePakketLabelId: carePackage.carePackagePakketLabelId,
                  carePackageName: carePackage.name, timeslots: [], isSubsidyPlace: carePackage.isSubsidyPlace };
            this.callback.carePackageSelected(locationPackage);
            this.data.packages.push(locationPackage);
        }
        this.reporter.partChanged(WizardPart.locations);
    }
}
